import { useEffect, useState } from 'react'
import { ContentState, DraftHandleValue, EditorState, RichUtils } from 'draft-js'
import { Editor, EditorProps } from 'react-draft-wysiwyg'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import { safeStateFromMarkdown } from './safeStateFromMarkdown'
import { stateToMarkdown } from 'draft-js-export-markdown'

const toolbar = {
  options: ['inline', 'list', 'link', 'history'],
  inline: {
    options: ['bold', 'italic', 'underline', 'strikethrough']
  },
  history: {
    // ensure undo/redo buttons are visible for quick reversal
    inDropdown: false
  }
}

const LIST_BLOCK_TYPES = ['unordered-list-item', 'ordered-list-item']

type RichTextEditorProps = EditorProps & {
  initial: string | null
  value: string | null
  onChange: (value: string) => void
  // external replace hook: when replaceKey changes, replace content with replaceMarkdown preserving undo history
  replaceMarkdown?: string | null
  replaceKey?: number
}

const RichTextEditor = ({
  initial,
  value,
  onChange,
  replaceMarkdown,
  replaceKey,
  ...rest
}: RichTextEditorProps): JSX.Element => {
  const [editorState, setEditorState] = useState(
    EditorState.createWithContent(safeStateFromMarkdown(initial || ''))
  )
  const [markdownCache, setMarkdownCache] = useState(initial || '')

  useEffect(() => {
    if (value !== null && value !== undefined && value !== markdownCache) {
      setEditorState(EditorState.createWithContent(safeStateFromMarkdown(value)))
      setMarkdownCache(value)
    }
  }, [value, markdownCache])

  // Apply external full-content replace as a single undoable step
  useEffect(() => {
    if (replaceKey === undefined) return
    if (replaceMarkdown === null || replaceMarkdown === undefined) return
    const newContent: ContentState = safeStateFromMarkdown(replaceMarkdown || '')
    const nextState = EditorState.push(editorState, newContent, 'insert-characters')
    setEditorState(nextState)
    setMarkdownCache(replaceMarkdown)
    onChange(replaceMarkdown)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replaceKey])

  const handleEditorStateChange = (newState: EditorState) => {
    // Export and normalize: remove excessive blank lines
    let markdown = stateToMarkdown(newState.getCurrentContent())
    // Collapse 3+ consecutive newlines to max 2
    markdown = markdown.replace(/\n{3,}/g, '\n\n')
    // Trim whitespace around
    markdown = markdown.trim()
    onChange(markdown)
    setMarkdownCache(markdown)
    setEditorState(newState)
  }

  // Pressing backspace at the start of a list item should remove the bullet
  // (convert it to a normal paragraph) instead of merging it into the line
  // above while keeping the list block type. Other commands fall back to the
  // default rich-text handling (e.g. Ctrl+B/I/U), which we must replicate here
  // because providing this handler overrides the library's built-in one.
  const handleKeyCommand = (command: string, state: EditorState): DraftHandleValue => {
    if (command === 'backspace') {
      const selection = state.getSelection()
      const atBlockStart = selection.isCollapsed() && selection.getStartOffset() === 0
      if (atBlockStart) {
        const blockType = state
          .getCurrentContent()
          .getBlockForKey(selection.getStartKey())
          .getType()
        if (LIST_BLOCK_TYPES.includes(blockType)) {
          handleEditorStateChange(RichUtils.toggleBlockType(state, blockType))
          return 'handled'
        }
      }
    }

    const newState = RichUtils.handleKeyCommand(state, command)
    if (newState) {
      handleEditorStateChange(newState)
      return 'handled'
    }
    return 'not-handled'
  }

  // Returning false here makes react-draft-wysiwyg skip its own paste parser
  // (html-to-draftjs), which mangles Draft's exported list markup by splitting
  // each <li>'s nested wrappers into an empty bullet plus a separate paragraph.
  // Draft.js then falls back to its native DraftPasteProcessor.processHTML,
  // which reconstructs lists (and other formatting) faithfully.
  const handlePastedText = (): boolean => false

  return (
    <Editor
      {...rest}
      toolbar={toolbar}
      editorState={editorState}
      toolbarClassName="toolbarClassName"
      wrapperClassName="wrapperClassName"
      editorClassName="editorClassName"
      handleKeyCommand={handleKeyCommand}
      handlePastedText={handlePastedText}
      onEditorStateChange={handleEditorStateChange}
    />
  )
}

export default RichTextEditor
